import React, { useEffect, useState } from "react";
import styles from "./AddJobModal.module.scss";
import classNames from "classnames/bind";
import { useLoadScript } from "@react-google-maps/api";
import { googlePlacesApiKey } from "~/utils/constants/env";

// Components
import AddJobModalHeader from "./components/AddJobModalHeader";
import AddJobModalActions from "./components/AddJobModalActions";
import {
  ContactTypeSelector,
  ContactSearchBox,
  ContactList,
  NewContactForm,
} from "./components/ContactSelectionStep";
import { JobAddressInput } from "./components/AddressStep";
import {
  JobBasicInfo,
  JobDetailsForm,
  JobTextAreas,
} from "./components/JobDetailsStep";

// Hooks
import { useJobForm } from "./hooks/useJobForm";
import { useJobModalSteps } from "./hooks/useJobModalSteps";
import { useCustomersSearch } from "./hooks/useCustomersSearch";
import { useJobData } from "./hooks/useJobData";

// Types
import {
  AddJobModalProps,
  AddressSuggestion,
} from "./types/add-job.types";
import crmApi, { ICustomer } from "~/apis/crm";
import { notify, handleApiError } from "~/utils/helper";
import {
  clearAddJobDraft,
  readAddJobDraft,
  writeAddJobDraft,
} from "./addJobDraft";
// Constants
import { JobStageOptions } from "./constants/add-job.constants";
import BaseModal from "~/components/common/base-modal/BaseModal";

const cx = classNames.bind(styles);

const AddJobModal: React.FC<AddJobModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
}) => {
  const [draft] = useState(readAddJobDraft);
  const {
    formData,
    errors,
    updateField,
    updateError,
    validateJobStep,
    validateDetailsStep,
    isDetailsStepValid,
  } = useJobForm(draft?.formData);

  const { modalStep, jobStep, setModalStep, setJobStep, handleBackStep } =
    useJobModalSteps(draft?.modalStep, draft?.jobStep);

  const {
    customers,
    searchText,
    setSearchText,
    selectedContact,
    setSelectedContact,
  } = useCustomersSearch(isOpen, jobStep, draft?.selectedContact);

  const { salesReps, tradeTypes } = useJobData(isOpen);

  // Local State
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AddressSuggestion | null>(null);
  const [customerAddress, setCustomerAddress] = useState<string>(
    draft?.customerAddress ?? ""
  );
  const [useCustomerAddress, setUseCustomerAddress] = useState<boolean>(
    draft?.useCustomerAddress ?? false
  );
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    writeAddJobDraft({
      formData,
      modalStep,
      jobStep,
      selectedContact,
      customerAddress,
      useCustomerAddress,
    });
  }, [
    formData,
    modalStep,
    jobStep,
    selectedContact,
    customerAddress,
    useCustomerAddress,
  ]);

  // Clear address step state when back from step 2 to step 1
  const clearAddressStepState = () => {
    setSelectedSuggestion(null);
    setUseCustomerAddress(false);
    updateField("latitude", 0);
    updateField("longitude", 0);
  };

  const clearStep2UIState = () => {
    setSelectedSuggestion(null);
    setUseCustomerAddress(false);
    updateField("latitude", 0);
    updateField("longitude", 0);
  };

  // Clear step 3 form fields when entering step 3
  const clearStep3Form = () => {
    updateField("jobNumber", "");
    updateField("jobName", "");
    updateField("jobType", "");
    updateField("salesRepId", "");
    updateField("leadSource", "");
    updateField("referralName", "");
    updateField("tradeType", []);
    updateField("shingle", "");
    updateField("customShingleColor", "");
    updateField("shinglesManufacturer", "");
    updateField("numberOfSquares", "");
    updateField("currentStage", "");
    updateField("pitch", "");
    updateField("jobDescription", "");
    updateField("notes", "");
    updateField("insuranceCompanyName", "");
    updateField("insuranceEmail", "");
    updateField("claimNumber", "");
    updateField("insurancePhoneNumber", "");
    updateField("adjusterName", "");
    updateField("policyType", "");
    // Clear all errors
    updateError("jobNumber", "");
    updateError("jobName", "");
    updateError("jobType", "");
    updateError("salesRepId", "");
    updateError("leadSource", "");
    updateError("referralName", "");
    updateError("tradeType", "");
    updateError("currentStage", "");
    updateError("shingle", "");
    updateError("shinglesManufacturer", "");
    updateError("numberOfSquares", "");
    updateError("pitch", "");
    updateError("insuranceCompanyName", "");
    updateError("insuranceEmail", "");
    updateError("claimNumber", "");
    updateError("insurancePhoneNumber", "");
    updateError("policyType", "");
    updateError("jobDescription", "");
  };

  const handleBackStepWithClear = () => {
    if (modalStep === 2) {
      clearAddressStepState();
      if (customerAddress) {
        updateField("address", customerAddress);
      }
    }
    handleBackStep();
  };

  const generateJobNumber = () => {
    const year = new Date().getFullYear();
    const randomNum = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `JOB-${year}-${randomNum}`;
  };

  const goToJobDetailsStep = () => {
    clearStep3Form();
    updateField("jobNumber", generateJobNumber());
    setModalStep(3);
  };

  // Google Maps
  const { isLoaded } = useLoadScript({
    googleMapsApiKey:
      googlePlacesApiKey || "AIzaSyDGQ-_dJ3HEO9pWMf_LzWO8wxH2Vce2i58",
  });

  // Handlers
  const handleContactTypeChange = (type: "Existing" | "New") => {
    setJobStep(type);
    updateField("existenceCustomer", type);
  };

  const handleSelectContact = (contact: ICustomer) => {
    const isSelected = selectedContact.some((c) => c.id === contact.id);

    if (isSelected) {
      // Deselect if already selected
      setSelectedContact([]);
    } else {
      setSelectedContact([contact]);
    }
  };

  const handleNextToAddressStep = async () => {
    if (jobStep === "Existing") {
      if (selectedContact.length > 0) {
        // Use first selected contact for address
        const firstContact = selectedContact[0];
        if (firstContact.address) {
          setCustomerAddress(firstContact.address);
        }
        clearStep2UIState();
        setModalStep(2);
      }
      return;
    } else if (jobStep === "New") {
      if (validateJobStep(jobStep)) {
        const placeId = selectedSuggestion?.placePrediction?.placeId;
        if (placeId) {
          try {
            const place = await readPlace(placeId);
            updateField("postalCode", place.postalCode);
          } catch (err) {
            updateField("postalCode", "");
          }
        }
        if (formData.address) {
          setCustomerAddress(formData.address);
        }
        clearStep2UIState();

        setModalStep(2);
      }
    }
  };

  const readPlace = async (placeId: string) => {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${placeId}`,
      {
        method: "GET",
        headers: {
          "X-Goog-Api-Key":
            googlePlacesApiKey || "AIzaSyBFvJr6l0XfUv2axQAvHaHnTzwzwlKJ1oo",
          "X-Goog-FieldMask": "location,addressComponents",
        },
      }
    );
    const data = await res.json();
    const postalCode =
      data?.addressComponents?.find(
        (component: { types?: string[]; longText?: string }) =>
          component.types?.includes("postal_code")
      )?.longText || "";
    return {
      latitude: data?.location?.latitude || 0,
      longitude: data?.location?.longitude || 0,
      postalCode,
    };
  };

  const handleAddressSuggestionSelect = (suggestion: AddressSuggestion) => {
    setSelectedSuggestion(suggestion);
    const newAddress = suggestion?.placePrediction?.text?.text || "";
    updateField("address", newAddress);
    updateField("latitude", 0);
    updateField("longitude", 0);
  };

  const handleConfirmAddress = async () => {
    // If using customer address (no suggestion selected)
    if (useCustomerAddress && customerAddress) {
      updateField("address", customerAddress);
      updateField("latitude", 0);
      updateField("longitude", 0);
      goToJobDetailsStep();
      return;
    }

    // If using Google Places suggestion
    if (selectedSuggestion) {
      const placeId = selectedSuggestion?.placePrediction?.placeId;
      const addressText = selectedSuggestion.placePrediction?.text?.text || "";
      if (placeId) {
        try {
          const place = await readPlace(placeId);
          updateField("latitude", place.latitude);
          updateField("longitude", place.longitude);
        } catch (err) {
          updateField("latitude", 0);
          updateField("longitude", 0);
        }
      }
      updateField("address", addressText);
      goToJobDetailsStep();
      return;
    }

    goToJobDetailsStep();
  };

  const handleSaveContact = async () => {
    const nameError = formData.name.trim() ? "" : "Name is required";
    const phoneError = !formData.phoneNumber
      ? "Phone number is required"
      : /^\d+$/.test(formData.phoneNumber)
      ? ""
      : "Phone number must be numeric";
    const emailError =
      formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ? "Email is invalid"
        : "";

    updateError("name", nameError);
    updateError("phoneNumber", phoneError);
    updateError("email", emailError);
    updateError("address", "");
    if (nameError || phoneError || emailError) return;

    setSavingContact(true);
    try {
      let zipCode = "";
      const placeId = selectedSuggestion?.placePrediction?.placeId;
      if (placeId) {
        try {
          const place = await readPlace(placeId);
          zipCode = place.postalCode;
        } catch (err) {
          zipCode = "";
        }
      }
      await crmApi.createCustomer({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        address: customerAddress || formData.address || "",
        latitude: formData.latitude || 0,
        longitude: formData.longitude || 0,
        zipCode,
      });
      clearAddJobDraft();
      notify("success", "Contact saved");
      onCancel();
    } catch (error) {
      handleApiError(error);
      setSavingContact(false);
    }
  };

  const handleCreateJob = () => {
    if (validateDetailsStep()) {
      // Transform formData to API format
      const transformData = (data: typeof formData) => {
        // Map currentStage to jobStage (Current Stage from step 3)
        // API requires jobStage to be value format (e.g., "LEAD", "APPOINTMENT_SCHEDULED")
        let jobStageValue = "LEAD"; // Default fallback (value format)
        if (data.currentStage) {
          // currentStage stores label from select, find the corresponding value
          const stageOption = JobStageOptions.find(
            (opt) =>
              opt.label === data.currentStage || opt.value === data.currentStage
          );
          jobStageValue = stageOption?.value || data.currentStage || "LEAD";
        }

        const transformed: any = {};

        // Step 1: flagExistenceCustomer
        if (jobStep === "Existing" && selectedContact.length > 0) {
          transformed.flagExistenceCustomer = true;
          transformed.primaryCustomerId = parseInt(
            selectedContact[0].id.toString()
          );
        } else {
          // New contact
          transformed.flagExistenceCustomer = false;
          
          // Combine firstName and lastName to name (API requires name, not firstName/lastName)
          let fullName = data.name || "";
          if (!fullName && (data.firstName || data.lastName)) {
            fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
          }
          // Ensure name is not empty (API requires name)
          if (!fullName) {
            fullName = "Unknown"; // Fallback if no name provided
          }

          transformed.name = fullName || "Unknown";
          transformed.email = data.email || "";
          transformed.phoneNumber = data.phoneNumber || "";
          transformed.address = customerAddress || data.address || "";
          transformed.latitude = data.latitude !== undefined && data.latitude !== null ? data.latitude : 0;
          transformed.longitude = data.longitude !== undefined && data.longitude !== null ? data.longitude : 0;
          transformed.zipCode = data.postalCode || "";
          transformed.needAppointment = data.needAppointment || false;
        }

        transformed.flagUseCustomerAddress = useCustomerAddress;

        if (!useCustomerAddress && data.address) {
          transformed.jobAddress = data.address;
          transformed.jobAddressLatitude = data.latitude || 0;
          transformed.jobAddressLongitude = data.longitude || 0;
        }

        const contactAddress =
          jobStep === "Existing"
            ? selectedContact[0]?.address || ""
            : customerAddress || "";
        transformed.flagAddNewBillingAddress = false;
        transformed.flagSameAsJobAddress = false;
        transformed.flagSameAsCustomerAddress = Boolean(contactAddress.trim());

        // Step 3: Standard fields
        transformed.jobNumber = data.jobNumber || "";
        transformed.jobTitle =
          data.jobName || data.jobDescription.trim() || "New Job";
        transformed.jobType = data.jobType || "";
        transformed.priority = data.priority || "MEDIUM";
        transformed.salesRepId = data.salesRepId ? parseInt(data.salesRepId) : 0;
        transformed.leadSource = data.leadSource || "";
        transformed.referralName = data.referralName || "";

        if (data.tradeType.length > 0) {
          transformed.tradeTypes = data.tradeType.map((id) => parseInt(id, 10));
        }
        
        transformed.color =
          data.shingle === "CUSTOM"
            ? (data.customShingleColor || "").trim()
            : data.shingle || "";
        transformed.shingleManufacturer = data.shinglesManufacturer || "";
        transformed.numberOfSquares = data.numberOfSquares || undefined;
        transformed.jobStage = jobStageValue;
        transformed.pitch = data.pitch || "";
        transformed.description = data.jobDescription || "";
        transformed.jobNote = data.notes || "";

        // Step 3: If jobType = Insurance, add insurance fields
        if (data.jobType === "Insurance") {
          transformed.insuranceCompanyName = data.insuranceCompanyName || "";
          transformed.insuranceEmail = data.insuranceEmail || "";
          transformed.insuranceClaimNumber = data.claimNumber || "";
          transformed.insuranceAdjusterName = data.adjusterName || "";
          transformed.insurancePhone = data.insurancePhoneNumber || "";
          transformed.insurancePolicyType = data.policyType || "";
        }

        // Remove undefined, null, or empty string fields (except for required fields)
        const fieldsToKeep: string[] = [];
        Object.keys(transformed).forEach((key) => {
          if (
            transformed[key] === undefined ||
            (transformed[key] === "" && !fieldsToKeep.includes(key))
          ) {
            delete transformed[key];
          }
        });

        return transformed;
      };

      const dataToSubmit = transformData(formData);
      onSubmit(dataToSubmit);
    }
  };

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    updateField(field, value);

    if (field === "name") {
      if (!value) {
        updateError("name", "Name is required");
      } else {
        updateError("name", "");
      }
    }
    // Validate email and phoneNumber on change
    if (field === "email") {
      if (!value) {
        updateError("email", "Email is required");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        updateError("email", "Email is invalid");
      } else {
        updateError("email", "");
      }
    }
    if (field === "phoneNumber") {
      if (!value) {
        updateError("phoneNumber", "Phone number is required");
      } else if (!/^\d+$/.test(value)) {
        updateError("phoneNumber", "Phone number must be numeric");
      } else {
        updateError("phoneNumber", "");
      }
    }
    if (field === "address") {
      updateError("address", "");
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  const renderFormContent = () => {
    switch (modalStep) {
      case 1:
        return (
          <div className={cx("select-contact-container")}>
            <ContactTypeSelector
              jobStep={jobStep}
              onChange={handleContactTypeChange}
            />

            {jobStep === "Existing" ? (
              <div>
                <ContactSearchBox value={searchText} onChange={setSearchText} />

                <div className={cx("existing-contact-section", "my-[12px]")}>
                  <h4 className={cx("title")}>Available Contacts</h4>
                </div>

                <ContactList
                  customers={customers}
                  selectedContacts={selectedContact}
                  onSelectContact={handleSelectContact}
                />
              </div>
            ) : (
              <NewContactForm
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
                onAddressChange={(value) => {
                  handleFieldChange("address", value);
                  setCustomerAddress(value);
                  if (value) {
                    updateError("address", "");
                  }
                }}
                onAddressSuggestionSelect={handleAddressSuggestionSelect}
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className={cx("job-address-container")}>
            <JobAddressInput
              value={formData.address}
              customerAddress={customerAddress}
              useCustomerAddress={useCustomerAddress}
              onChange={(value) => {
                handleFieldChange("address", value);
                if (useCustomerAddress) {
                  setUseCustomerAddress(false);
                }
              }}
              onSuggestionSelect={(suggestion) => {
                handleAddressSuggestionSelect(suggestion);
                if (useCustomerAddress) {
                  setUseCustomerAddress(false);
                }
              }}
              onUseCustomerAddressChange={(checked) => {
                setUseCustomerAddress(checked);
                if (checked && customerAddress) {
                  handleFieldChange("address", customerAddress);
                  handleFieldChange("latitude", 0);
                  handleFieldChange("longitude", 0);
                  setSelectedSuggestion(null);
                }
              }}
            />
          </div>
        );

      case 3:
        // Get customer name for auto-generating job name
        let customerName = "";
        if (jobStep === "Existing" && selectedContact.length > 0) {
          customerName = selectedContact[0].name;
        } else if (jobStep === "New") {
          customerName = formData.name;
        }

        return (
          <div className={cx("job-details-container")}>
            <div className={cx("form-content")}>
              <div className={cx("job-details-form-content")}>
                <JobBasicInfo
                  formData={formData}
                  errors={errors}
                  salesReps={salesReps}
                  customerName={customerName}
                  onFieldChange={handleFieldChange}
                  onErrorFieldChange={updateError}
                />

                <div className={cx("vertical-divider")}></div>

                <JobDetailsForm
                  formData={formData}
                  errors={errors}
                  tradeTypes={tradeTypes}
                  onFieldChange={handleFieldChange}
                  onErrorFieldChange={updateError}
                />
              </div>

              <JobTextAreas
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
                onErrorFieldChange={updateError}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <BaseModal open={true} onClose={onCancel} closeOnOverlay={false}>
      <div className={cx("add-job-container")}>
        <AddJobModalHeader currentStep={modalStep} onClose={onCancel} />
        <div className={cx("body")}>
          {renderFormContent()}
        </div>
        <AddJobModalActions
          currentStep={modalStep}
          jobStep={jobStep}
          onBack={handleBackStepWithClear}
          onAddContact={handleSaveContact}
          addContactDisabled={savingContact}
          onContinue={
            modalStep === 2
              ? handleConfirmAddress
              : modalStep === 3
              ? handleCreateJob
              : handleNextToAddressStep
          }
          disabled={
            modalStep === 1
              ? jobStep === "Existing"
                ? selectedContact.length === 0
                : !formData.name ||
                  !formData.phoneNumber ||
                  !formData.email ||
                  !/^\d+$/.test(formData.phoneNumber) ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
              : modalStep === 3
              ? !isDetailsStepValid()
              : false
          }
        />
      </div>
    </BaseModal>
  );
};

export default AddJobModal;
